import { useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useScrollToBottom } from '../../../../utils/hooks/useScrollToBottom';

import { LoadingPage } from '../../../LoadingPage';
import { ErrorPage } from '../../../ErrorPage';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import { getOrCreateBrowserKey } from '../../../../utils/fingerprint_utils';
import { Button } from '../components/Button';
import { PoweredByQITech } from '../components/PoweredByQITech';
import { ThemedInput } from '../components/ThemedInput';
import * as S from './styles';

interface EnrichPersonalDataProps {
    signData: any;
    setSignerStep: (step: string) => void;
    setSignData: (data: any) => void;
}

const SIGNED_STATUSES = ['signed', 'sending_sign_receipt', 'receipt_sent', 'analyzing', 'analyzed', 'completed'];

export const SocialSecurityBenefitCardEnrichPersonalData = ({
    signData,
    setSignerStep,
    setSignData,
}: EnrichPersonalDataProps) => {
    const { clientThemeSettings } = useThemeContext();
    const { signerHash } = useParams<{ signerHash: string }>();

    const [loading, setLoading] = useState(false);
    const { contentEndRef, hasScrolledToEnd, scrollToBottom } = useScrollToBottom([loading]);
    const [error, setError] = useState<{ has_error: boolean; status: number }>({
        has_error: false,
        status: 0,
    });
    const [hasMadeRequest, setHasMadeRequest] = useState(false);
    const [hasClosedValidationScreen, setHasClosedValidationScreen] = useState(false);
    const [focusedOnce, setFocusedOnce] = useState<Record<string, boolean>>({});
    const [postalCodeLoading, setPostalCodeLoading] = useState(false);

    const [address, setAddress] = useState<any>(
        signData?.signer_data?.address ?? {
            street: '',
            number: '',
            complement: '',
            neighborhood: '',
            city: '',
            uf: 'SP',
            postal_code: '',
        },
    );

    const accentColor = clientThemeSettings?.font_color || '#0e3573';

    const validatePostalCode = (postalCode: string) => /^\d{5}-\d{3}$/.test(postalCode);

    const formatCEP = (value: string): string => {
        if (!value) return '';
        const digits = value.replace(/\D/g, '');
        return digits.replace(/(\d{5})(\d)/, '$1-$2').slice(0, 9);
    };

    const formatAddressNumber = (value: string): string => {
        if (!value) return '';
        return value.replace(/\D/g, '').slice(0, 5);
    };

    const isAddressValid = () =>
        address.street.length > 0 &&
        address.number.length > 0 &&
        address.neighborhood.length > 0 &&
        address.city.length > 0 &&
        address.uf.length > 0 &&
        address.postal_code.length > 0;

    const handlePostalCodeSearch = async (postalCode: string) => {
        setPostalCodeLoading(true);
        try {
            const config = { headers: { Authorization: signData.signer_token } };
            const response = await axios.get(`/signer/postal_code?postal_code=${postalCode}`, config);
            const data = response.data;
            const addressObject = {
                postal_code: postalCode,
                street: data.address ?? '',
                neighborhood: data.neighborhood ?? '',
                city: data.city ?? '',
                uf: data.short_state_name ?? '',
            };
            setAddress((prev: any) => ({ ...prev, ...addressObject }));
        } catch (err) {
            console.warn(err);
        } finally {
            setPostalCodeLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (hasMadeRequest || !isAddressValid()) return;

        let browserKey: string | null = null;
        try {
            browserKey = await getOrCreateBrowserKey();
        } catch (_) {}

        const payload = {
            event_date: new Date().toISOString(),
            step: 'enrich_personal_data',
            address: {
                street: address.street.trim(),
                number: address.number.trim(),
                complement: address.complement ? address.complement.trim() : undefined,
                neighborhood: address.neighborhood.trim(),
                city: address.city.trim(),
                uf: address.uf.trim(),
                postal_code: address.postal_code.trim(),
            },
            device_data: { device_key: browserKey },
        };

        const config = { headers: { Authorization: signData.signer_token } };

        try {
            setLoading(true);
            setHasMadeRequest(true);
            const response = await axios.patch('/signer', payload, config);
            setSignData({ ...signData, ...response.data });

            if (response.data.next_step) {
                setSignerStep(response.data.next_step);
            } else {
                const getResponse = await axios.get(`/signer/hash/${signerHash}`);
                const status = getResponse.data?.status;
                if (SIGNED_STATUSES.includes(status)) {
                    setSignerStep('signed');
                } else if (getResponse.data?.next_step) {
                    setSignerStep(getResponse.data.next_step);
                }
            }
        } catch (err: any) {
            const status = [401, 403, 409].includes((err?.response || {}).status)
                ? err.response.status
                : 500;
            setError({ has_error: true, status });
            setHasMadeRequest(false);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <LoadingPage clientThemeSettings={clientThemeSettings} />;

    if (error.has_error) {
        return (
            <ErrorPage
                error={error}
                redirectToLogin={() => setSignerStep('login')}
                resetError={() => setError({ has_error: false, status: 0 })}
                clientThemeSettings={clientThemeSettings}
            />
        );
    }

    const isValidationScreen = !hasClosedValidationScreen;

    return (
        <S.PageWrapper>
            <S.ScrollContent>
                <S.Header>
                    {clientThemeSettings?.logo_url && (
                        <S.LogoImage src={clientThemeSettings.logo_url} alt="logo" />
                    )}
                </S.Header>

                <S.HeroSection>
                    <S.Title $color={accentColor}>
                        {isValidationScreen ? (
                            <>
                                Confirme o endereço da{' '}
                                <strong>entrega do cartão</strong>
                            </>
                        ) : (
                            <>
                                Atualize o endereço de{' '}
                                <strong>entrega do cartão</strong>
                            </>
                        )}
                    </S.Title>
                    <S.Description $color={accentColor}>
                        {isValidationScreen
                            ? 'Certifique-se de que as informações estão corretas e prossiga:'
                            : 'Certifique-se de que o endereço de entrega do cartão está correto e que você conseguirá recebê-lo nesse local:'}
                    </S.Description>
                </S.HeroSection>

                <S.FormSection>
                    {isValidationScreen ? (
                        <>
                            <S.ReadOnlyField>
                                <S.FieldLabel $accentColor={accentColor}>CEP</S.FieldLabel>
                                <S.FieldInput value={address.postal_code || ''} readOnly />
                            </S.ReadOnlyField>
                            <S.ReadOnlyField>
                                <S.FieldLabel $accentColor={accentColor}>Cidade</S.FieldLabel>
                                <S.FieldInput value={address.city || ''} readOnly />
                            </S.ReadOnlyField>
                            <S.ReadOnlyField>
                                <S.FieldLabel $accentColor={accentColor}>Estado</S.FieldLabel>
                                <S.FieldInput value={address.uf || ''} readOnly />
                            </S.ReadOnlyField>
                            <S.ReadOnlyField>
                                <S.FieldLabel $accentColor={accentColor}>Bairro</S.FieldLabel>
                                <S.FieldInput value={address.neighborhood || ''} readOnly />
                            </S.ReadOnlyField>
                            <S.ReadOnlyField>
                                <S.FieldLabel $accentColor={accentColor}>Endereço</S.FieldLabel>
                                <S.FieldInput value={address.street || ''} readOnly />
                            </S.ReadOnlyField>
                            <S.ReadOnlyField>
                                <S.FieldLabel $accentColor={accentColor}>Número</S.FieldLabel>
                                <S.FieldInput value={address.number || ''} readOnly />
                            </S.ReadOnlyField>
                            <S.ReadOnlyField>
                                <S.FieldLabel $accentColor={accentColor}>Complemento</S.FieldLabel>
                                <S.FieldInput value={address.complement || ''} readOnly />
                            </S.ReadOnlyField>
                        </>
                    ) : (
                        <>
                            <ThemedInput
                                labelValue="CEP"
                                labelColor={accentColor}
                                fieldValue={address.postal_code}
                                hasError={focusedOnce.postalCode && address.postal_code.length === 0}
                                errorMessage=""
                                type="numeric"
                                onChange={(e) => {
                                    setFocusedOnce({ ...focusedOnce, postalCode: true });
                                    const formatted = formatCEP(e.target.value);
                                    setAddress((prev: any) => ({ ...prev, postal_code: formatted }));
                                    if (validatePostalCode(formatted) && !postalCodeLoading) {
                                        handlePostalCodeSearch(formatted);
                                    }
                                }}
                                onBlur={() => {}}
                            />
                            <ThemedInput
                                labelValue="Cidade"
                                labelColor={accentColor}
                                fieldValue={address.city}
                                hasError={focusedOnce.city && address.city.length === 0}
                                errorMessage=""
                                type="text"
                                onChange={(e) => {
                                    setFocusedOnce({ ...focusedOnce, city: true });
                                    setAddress((prev: any) => ({ ...prev, city: e.target.value }));
                                }}
                                onBlur={() => {}}
                            />
                            <S.SelectGroup>
                                <S.SelectLabel $accentColor={accentColor}>Estado</S.SelectLabel>
                                <S.StyledSelect
                                    value={address.uf}
                                    onChange={(e) =>
                                        setAddress((prev: any) => ({ ...prev, uf: e.target.value }))
                                    }
                                >
                                    <option value="AC">Acre (AC)</option>
                                    <option value="AL">Alagoas (AL)</option>
                                    <option value="AM">Amazonas (AM)</option>
                                    <option value="AP">Amapá (AP)</option>
                                    <option value="BA">Bahia (BA)</option>
                                    <option value="CE">Ceará (CE)</option>
                                    <option value="DF">Distrito Federal (DF)</option>
                                    <option value="ES">Espírito Santo (ES)</option>
                                    <option value="GO">Goiás (GO)</option>
                                    <option value="MA">Maranhão (MA)</option>
                                    <option value="MG">Minas Gerais (MG)</option>
                                    <option value="MS">Mato Grosso do Sul (MS)</option>
                                    <option value="MT">Mato Grosso (MT)</option>
                                    <option value="PA">Pará (PA)</option>
                                    <option value="PB">Paraíba (PB)</option>
                                    <option value="PE">Pernambuco (PE)</option>
                                    <option value="PI">Piauí (PI)</option>
                                    <option value="PR">Paraná (PR)</option>
                                    <option value="RJ">Rio de Janeiro (RJ)</option>
                                    <option value="RN">Rio Grande do Norte (RN)</option>
                                    <option value="RO">Rondônia (RO)</option>
                                    <option value="RR">Roraima (RR)</option>
                                    <option value="RS">Rio Grande do Sul (RS)</option>
                                    <option value="SC">Santa Catarina (SC)</option>
                                    <option value="SE">Sergipe (SE)</option>
                                    <option value="SP">São Paulo (SP)</option>
                                    <option value="TO">Tocantins (TO)</option>
                                </S.StyledSelect>
                            </S.SelectGroup>
                            <ThemedInput
                                labelValue="Bairro"
                                labelColor={accentColor}
                                fieldValue={address.neighborhood}
                                hasError={focusedOnce.neighborhood && address.neighborhood.length === 0}
                                errorMessage=""
                                type="text"
                                onChange={(e) => {
                                    setFocusedOnce({ ...focusedOnce, neighborhood: true });
                                    setAddress((prev: any) => ({
                                        ...prev,
                                        neighborhood: e.target.value,
                                    }));
                                }}
                                onBlur={() => {}}
                            />
                            <ThemedInput
                                labelValue="Endereço"
                                labelColor={accentColor}
                                fieldValue={address.street}
                                hasError={focusedOnce.street && address.street.length === 0}
                                errorMessage=""
                                type="text"
                                onChange={(e) => {
                                    setFocusedOnce({ ...focusedOnce, street: true });
                                    setAddress((prev: any) => ({ ...prev, street: e.target.value }));
                                }}
                                onBlur={() => {}}
                            />
                            <ThemedInput
                                labelValue="Número"
                                labelColor={accentColor}
                                fieldValue={address.number}
                                hasError={
                                    focusedOnce.number &&
                                    (address.number.length === 0 || address.number.length > 5)
                                }
                                errorMessage=""
                                type="numeric"
                                onChange={(e) => {
                                    setFocusedOnce({ ...focusedOnce, number: true });
                                    setAddress((prev: any) => ({
                                        ...prev,
                                        number: formatAddressNumber(e.target.value),
                                    }));
                                }}
                                onBlur={() => {}}
                            />
                            <ThemedInput
                                labelValue="Complemento"
                                labelColor={accentColor}
                                fieldValue={address.complement || ''}
                                hasError={false}
                                errorMessage=""
                                type="text"
                                onChange={(e) => {
                                    setAddress((prev: any) => ({
                                        ...prev,
                                        complement: e.target.value,
                                    }));
                                }}
                                onBlur={() => {}}
                            />
                        </>
                    )}
                </S.FormSection>
                <div ref={contentEndRef} />
            </S.ScrollContent>

            <S.FooterSection>
                {isValidationScreen ? (
                    <S.ButtonRow>
                        <Button
                            clientThemeSettings={clientThemeSettings}
                            onClick={() => setHasClosedValidationScreen(true)}
                            variant="soft"
                        >
                            Alterar endereço
                        </Button>
                        <Button
                            clientThemeSettings={clientThemeSettings}
                            onClick={hasScrolledToEnd ? handleSubmit : scrollToBottom}
                        >
                            Confirmar
                        </Button>
                    </S.ButtonRow>
                ) : (
                    <Button
                        clientThemeSettings={clientThemeSettings}
                        onClick={hasScrolledToEnd ? handleSubmit : scrollToBottom}
                        disabled={!isAddressValid()}
                    >
                        Confirmar
                    </Button>
                )}
                <PoweredByQITech />
            </S.FooterSection>
        </S.PageWrapper>
    );
};
